import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import * as yup from "yup";
import { getBrandUpdateURL } from "apis/brand";
import EditBrandProfile from "./EditBrandProfile";
import { TOAST_CONFIG, UPLOAD_TO_S3 } from "../../constants";
import {
  appendJsonToFormData,
  prependHTTPsForSocialLinks,
  handleSetErrorAndSwitchFormState,
  validationForURLPattern,
  validateInputImageWithExistingImageUrl,
} from "../../utils/HelperFunctions";
import { put, patch } from "../../utils/request";

type FormFieldsType =
  | "name"
  | "about"
  | "country"
  | "state"
  | "city"
  | "zipcode"
  | "website_link"
  | "instagram_link"
  | "twitter_link"
  | "other_link"
  | "profile_image_url"
  | "cover_image_url";

const formFields = {
  name: "0",
  about: "0",
  country: "0",
  state: "0",
  city: "0",
  zipcode: "0",
  website_link: "0",
  instagram_link: "0",
  twitter_link: "0",
  other_link: "0",
  profile_image_url: "0",
  cover_image_url: "0",
};

const schema = yup.object().shape({
  profile_image: validateInputImageWithExistingImageUrl("profile_image_url"),
  cover_image: validateInputImageWithExistingImageUrl("cover_image_url"),
  name: yup.string().trim().required("* Required field"),
  about: yup.string().trim().required("* Required field"),
  country: yup.string().trim().required("Please Select Country"),
  state: yup.string().trim().required("Please Select State"),
  city: yup.string().trim().required("Please Enter City"),
  zipcode: yup
    .string()
    .trim()
    .max(15, "Zip Code Cannot Be Longer Than 15 Characters")
    .nullable()
    .required("* Required"),
  website_link: validationForURLPattern("website_link").required("* Required"),
  instagram_link: validationForURLPattern("instagram_link", false),
  twitter_link: validationForURLPattern("twitter_link", false),
  other_link: validationForURLPattern("other_link", false),
});

function EditBrandProfilePage({ brandDetails, revalidateBrandDetails, brandId }: any) {
  const [addressState, setAddressState] = useState({ country: "", state: [] });

  const [loading, setLoading] = useState(false);
  const formMethods = useForm({
    resolver: yupResolver(schema),
    defaultValues: brandDetails,
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    getValues,
    formState: { errors },
  } = formMethods;

  const handleBrandUpdateSuccess = () => {
    toast.success("Updated Brand details", TOAST_CONFIG);
    revalidateBrandDetails();
    setLoading(false);
  };
  function checkForChangeInImages(image: any) {
    if (image) {
      return put(UPLOAD_TO_S3, appendJsonToFormData(image));
    }
    return true;
  }

  const handleBrandUpdateError = (errorMessage: string, error: any, errorData: any) => {
    toast.error(errorMessage, TOAST_CONFIG);
    handleSetErrorAndSwitchFormState<FormFieldsType>(errorData, formFields, setError);
    setLoading(false);
  };

  async function getPayload() {
    const formValues = getValues();
    const formValuesWithHTTPSAppendedForSocialLinks = prependHTTPsForSocialLinks(formValues);
    const imagesToPost = [formValues.profile_image?.[0], formValues.cover_image?.[0]];
    const response = await Promise.allSettled(imagesToPost.map((image) => checkForChangeInImages(image)));
    const [profileImageUrl, coverImageUrl] = response
      // @ts-ignore
      .map((r) => r.value.file_url);
    const data = Object.assign(formValuesWithHTTPSAppendedForSocialLinks, {
      profile_image_url: profileImageUrl,
      cover_image_url: coverImageUrl,
    });
    delete data.id;
    delete data.created_at;
    delete data.is_approved;
    delete data.name;
    delete data.tags;

    // payload consist of profile image and cover image which should not be included in payload
    delete data.profile_image;
    delete data.cover_image;
    return data;
  }

  const handleUpdateBrandProfile = async () => {
    setLoading(true);
    const payload = await getPayload();
    patch(getBrandUpdateURL(brandId), payload, {
      processData: handleBrandUpdateSuccess,
      processError: handleBrandUpdateError,
    });
  };

  useEffect(() => {
    setAddressState(brandDetails.state);
  }, []);

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...formMethods}>
      <EditBrandProfile
        loading={loading}
        handleFormSubmit={handleSubmit(handleUpdateBrandProfile)}
        register={register}
        errors={errors}
        watch={watch}
        setValue={setValue}
        getValues={getValues}
        addressState={addressState}
        setAddressState={setAddressState}
      />
    </FormProvider>
  );
}

export default EditBrandProfilePage;
